import React, { useState, useCallback, useMemo, useEffect } from 'react';

type ConflictRegion = {
  id: string;
  startLine: number;
  endLine: number;
  baseContent: string;
  localContent: string;
  remoteContent: string;
  resolved: boolean;
  resolution: 'local' | 'remote' | 'both' | 'custom' | null;
};

type MergeFile = {
  path: string;
  baseContent: string;
  localContent: string;
  remoteContent: string;
};

type ThreeWayMergeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  files: MergeFile[];
  onMergeComplete: (path: string, mergedContent: string) => void;
  onMergeAllComplete: (results: Array<{ path: string; content: string }>) => void;
};

// Simple line-based diff to find conflict regions
function findConflictRegions(
  base: string,
  local: string,
  remote: string
): ConflictRegion[] {
  const baseLines = base.split('\n');
  const localLines = local.split('\n');
  // Remote lines used for comparison below
  const _remoteLines = remote.split('\n');

  const conflicts: ConflictRegion[] = [];
  let conflictId = 0;

  // Simple conflict detection: compare lines
  const maxLen = Math.max(baseLines.length, localLines.length, _remoteLines.length);
  let inConflict = false;
  let conflictStart = 0;
  let conflictBase: string[] = [];
  let conflictLocal: string[] = [];
  let conflictRemote: string[] = [];

  for (let i = 0; i <= maxLen; i++) {
    const baseLine = baseLines[i] ?? '';
    const localLine = localLines[i] ?? '';
    const remoteLine = _remoteLines[i] ?? '';

    // Check if there's a difference
    const hasLocalChange = baseLine !== localLine;
    const hasRemoteChange = baseLine !== remoteLine;
    const isConflicting = hasLocalChange && hasRemoteChange && localLine !== remoteLine;

    if (isConflicting && i < maxLen) {
      if (!inConflict) {
        inConflict = true;
        conflictStart = i;
        conflictBase = [];
        conflictLocal = [];
        conflictRemote = [];
      }
      conflictBase.push(baseLine);
      conflictLocal.push(localLine);
      conflictRemote.push(remoteLine);
    } else if (inConflict) {
      conflicts.push({
        id: `conflict-${conflictId++}`,
        startLine: conflictStart,
        endLine: conflictStart + conflictBase.length - 1,
        baseContent: conflictBase.join('\n'),
        localContent: conflictLocal.join('\n'),
        remoteContent: conflictRemote.join('\n'),
        resolved: false,
        resolution: null,
      });
      inConflict = false;
    }
  }

  return conflicts;
}

// Apply resolutions and generate merged content
function generateMergedContent(
  base: string,
  local: string,
  remote: string,
  regions: ConflictRegion[]
): string {
  if (regions.length === 0) {
    // No conflicts, prefer local changes
    return local;
  }

  const localLines = local.split('\n');
  const remoteLines = remote.split('\n');
  const result: string[] = [];

  let lastEnd = -1;

  for (const region of regions) {
    // Add lines before this conflict from local
    for (let i = lastEnd + 1; i < region.startLine; i++) {
      result.push(localLines[i] ?? '');
    }

    // Add resolved content
    if (region.resolved) {
      switch (region.resolution) {
        case 'local':
          result.push(region.localContent);
          break;
        case 'remote':
          result.push(region.remoteContent);
          break;
        case 'both':
          result.push(region.localContent);
          result.push(region.remoteContent);
          break;
        case 'custom':
          // Custom should already be in localContent
          result.push(region.localContent);
          break;
      }
    } else {
      // Unresolved - add conflict markers
      result.push('<<<<<<< LOCAL');
      result.push(region.localContent);
      result.push('=======');
      result.push(region.remoteContent);
      result.push('>>>>>>> REMOTE');
    }

    lastEnd = region.endLine;
  }

  // Add remaining lines
  for (let i = lastEnd + 1; i < localLines.length; i++) {
    result.push(localLines[i]);
  }

  return result.join('\n');
}

export const ThreeWayMergeModal = React.memo(function ThreeWayMergeModal({
  isOpen,
  onClose,
  files,
  onMergeComplete,
  onMergeAllComplete,
}: ThreeWayMergeModalProps) {
  const [selectedFile, setSelectedFile] = useState<MergeFile | null>(files[0] || null);
  const [conflictRegions, setConflictRegions] = useState<Map<string, ConflictRegion[]>>(new Map());
  const [mergedContent, setMergedContent] = useState<Map<string, string>>(new Map());
  const [viewMode, setViewMode] = useState<'3-way' | 'result'>('3-way');
  const [activeConflictIndex, setActiveConflictIndex] = useState(0);

  // Initialize conflict regions for all files
  useEffect(() => {
    const regions = new Map<string, ConflictRegion[]>();
    const merged = new Map<string, string>();

    for (const file of files) {
      const fileRegions = findConflictRegions(
        file.baseContent,
        file.localContent,
        file.remoteContent
      );
      regions.set(file.path, fileRegions);
      merged.set(file.path, file.localContent);
    }

    setConflictRegions(regions);
    setMergedContent(merged);
  }, [files]);

  // Get current file's conflicts
  const currentConflicts = useMemo(() => {
    if (!selectedFile) return [];
    return conflictRegions.get(selectedFile.path) || [];
  }, [selectedFile, conflictRegions]);

  // Get current merged content
  const currentMerged = useMemo(() => {
    if (!selectedFile) return '';
    return mergedContent.get(selectedFile.path) || '';
  }, [selectedFile, mergedContent]);

  // Check if all conflicts are resolved
  const allResolved = useMemo(() => {
    const entries = Array.from(conflictRegions.values());
    for (const regions of entries) {
      if (regions.some(r => !r.resolved)) {
        return false;
      }
    }
    return true;
  }, [conflictRegions]);

  // Resolve a conflict
  const resolveConflict = useCallback((
    conflictId: string,
    resolution: 'local' | 'remote' | 'both'
  ) => {
    if (!selectedFile) return;

    setConflictRegions(prev => {
      const newRegions = new Map(prev);
      const fileRegions = [...(newRegions.get(selectedFile.path) || [])];
      const idx = fileRegions.findIndex(r => r.id === conflictId);

      if (idx !== -1) {
        fileRegions[idx] = {
          ...fileRegions[idx],
          resolved: true,
          resolution,
        };
        newRegions.set(selectedFile.path, fileRegions);

        // Update merged content
        const newMerged = generateMergedContent(
          selectedFile.baseContent,
          selectedFile.localContent,
          selectedFile.remoteContent,
          fileRegions
        );
        setMergedContent(m => new Map(m).set(selectedFile.path, newMerged));
      }

      return newRegions;
    });

    // Move to next conflict
    if (activeConflictIndex < currentConflicts.length - 1) {
      setActiveConflictIndex(activeConflictIndex + 1);
    }
  }, [selectedFile, activeConflictIndex, currentConflicts.length]);

  // Resolve all conflicts in current file
  const resolveAllInFile = useCallback((resolution: 'local' | 'remote') => {
    if (!selectedFile) return;

    setConflictRegions(prev => {
      const newRegions = new Map(prev);
      const fileRegions = (newRegions.get(selectedFile.path) || []).map(r => ({
        ...r,
        resolved: true,
        resolution,
      }));
      newRegions.set(selectedFile.path, fileRegions);

      const newMerged = generateMergedContent(
        selectedFile.baseContent,
        selectedFile.localContent,
        selectedFile.remoteContent,
        fileRegions
      );
      setMergedContent(m => new Map(m).set(selectedFile.path, newMerged));

      return newRegions;
    });
  }, [selectedFile]);

  // Save current file merge
  const saveMerge = useCallback(() => {
    if (!selectedFile) return;
    onMergeComplete(selectedFile.path, currentMerged);

    // Move to next file with conflicts
    const nextFile = files.find(f =>
      f.path !== selectedFile.path &&
      (conflictRegions.get(f.path) || []).some(r => !r.resolved)
    );
    if (nextFile) {
      setSelectedFile(nextFile);
      setActiveConflictIndex(0);
    }
  }, [selectedFile, currentMerged, files, conflictRegions, onMergeComplete]);

  // Save all merges
  const saveAllMerges = useCallback(() => {
    const results = files.map(f => ({
      path: f.path,
      content: mergedContent.get(f.path) || f.localContent,
    }));
    onMergeAllComplete(results);
    onClose();
  }, [files, mergedContent, onMergeAllComplete, onClose]);

  // Update merged content directly (for manual edits)
  const handleMergedContentChange = useCallback((content: string) => {
    if (!selectedFile) return;
    setMergedContent(m => new Map(m).set(selectedFile.path, content));
  }, [selectedFile]);

  const activeConflict = currentConflicts[activeConflictIndex];

  const getFileStats = useCallback((path: string) => {
    const regions = conflictRegions.get(path) || [];
    const resolved = regions.filter(r => r.resolved).length;
    return { total: regions.length, resolved };
  }, [conflictRegions]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-modal-title"
        style={{
          maxWidth: '95vw',
          width: '1200px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="modal-header">
          <div className="modal-title" id="merge-modal-title">
            3-Way Merge Editor
            <span style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '12px', color: 'var(--muted)' }}>
              {files.length} file{files.length !== 1 ? 's' : ''} with conflicts
            </span>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: 0, flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* File list sidebar */}
          <div
            style={{
              width: '180px',
              borderRight: '1px solid var(--border)',
              overflow: 'auto',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                padding: '8px 12px',
                fontSize: '11px',
                fontWeight: 'bold',
                color: 'var(--muted)',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-secondary)',
              }}
            >
              Files with Conflicts
            </div>
            {files.map((file) => {
              const stats = getFileStats(file.path);
              return (
                <button
                  key={file.path}
                  onClick={() => {
                    setSelectedFile(file);
                    setActiveConflictIndex(0);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    background: selectedFile?.path === file.path ? 'var(--accent)' : 'transparent',
                    color: selectedFile?.path === file.path ? 'white' : 'var(--text)',
                    fontSize: '12px',
                    borderBottom: '1px solid var(--border)',
                    border: 'none',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontWeight: 500 }}>
                    {file.path.split('/').pop()}
                  </div>
                  <div
                    style={{
                      fontSize: '10px',
                      opacity: 0.7,
                      marginTop: '2px',
                    }}
                  >
                    {stats.total === 0 ? (
                      <span style={{ color: 'var(--success)' }}>No conflicts</span>
                    ) : stats.resolved === stats.total ? (
                      <span style={{ color: 'var(--success)' }}>All resolved</span>
                    ) : (
                      <span>{stats.resolved}/{stats.total} resolved</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Main content area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedFile ? (
              <>
                {/* Toolbar */}
                <div
                  style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: 'var(--bg-secondary)',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
                    {selectedFile.path}
                  </span>

                  <div style={{ flex: 1 }} />

                  {/* View mode toggle */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      className="pill-btn"
                      onClick={() => setViewMode('3-way')}
                      style={{
                        background: viewMode === '3-way' ? 'var(--accent)' : 'var(--bg)',
                        color: viewMode === '3-way' ? 'white' : 'var(--text)',
                        fontSize: '11px',
                        padding: '4px 10px',
                      }}
                    >
                      3-Way View
                    </button>
                    <button
                      className="pill-btn"
                      onClick={() => setViewMode('result')}
                      style={{
                        background: viewMode === 'result' ? 'var(--accent)' : 'var(--bg)',
                        color: viewMode === 'result' ? 'white' : 'var(--text)',
                        fontSize: '11px',
                        padding: '4px 10px',
                      }}
                    >
                      Result
                    </button>
                  </div>

                  {/* Conflict navigation */}
                  {currentConflicts.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        className="pill-btn"
                        onClick={() => setActiveConflictIndex(Math.max(0, activeConflictIndex - 1))}
                        disabled={activeConflictIndex === 0}
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Prev
                      </button>
                      <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                        Conflict {activeConflictIndex + 1}/{currentConflicts.length}
                      </span>
                      <button
                        className="pill-btn"
                        onClick={() => setActiveConflictIndex(Math.min(currentConflicts.length - 1, activeConflictIndex + 1))}
                        disabled={activeConflictIndex === currentConflicts.length - 1}
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>

                {viewMode === '3-way' ? (
                  <>
                    {/* 3-way diff view */}
                    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                      {/* Base version */}
                      <div style={{ flex: 1, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <div
                          style={{
                            padding: '8px 12px',
                            background: 'var(--bg-secondary)',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: 'var(--muted)',
                            borderBottom: '1px solid var(--border)',
                          }}
                        >
                          Base (Common Ancestor)
                        </div>
                        <pre
                          style={{
                            flex: 1,
                            margin: 0,
                            padding: '12px',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            background: 'var(--bg)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {activeConflict?.baseContent || selectedFile.baseContent || '(no base)'}
                        </pre>
                      </div>

                      {/* Local version */}
                      <div style={{ flex: 1, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <div
                          style={{
                            padding: '8px 12px',
                            background: 'var(--bg-secondary)',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: 'var(--success)',
                            borderBottom: '1px solid var(--border)',
                          }}
                        >
                          Local (Yours)
                        </div>
                        <pre
                          style={{
                            flex: 1,
                            margin: 0,
                            padding: '12px',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            background: activeConflict?.resolved && activeConflict.resolution === 'local'
                              ? 'rgba(76, 175, 80, 0.1)'
                              : 'var(--bg)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {activeConflict?.localContent || selectedFile.localContent}
                        </pre>
                      </div>

                      {/* Remote version */}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <div
                          style={{
                            padding: '8px 12px',
                            background: 'var(--bg-secondary)',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: 'var(--warning)',
                            borderBottom: '1px solid var(--border)',
                          }}
                        >
                          Remote (Theirs)
                        </div>
                        <pre
                          style={{
                            flex: 1,
                            margin: 0,
                            padding: '12px',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            background: activeConflict?.resolved && activeConflict.resolution === 'remote'
                              ? 'rgba(255, 152, 0, 0.1)'
                              : 'var(--bg)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {activeConflict?.remoteContent || selectedFile.remoteContent}
                        </pre>
                      </div>
                    </div>

                    {/* Conflict resolution buttons */}
                    {activeConflict && !activeConflict.resolved && (
                      <div
                        style={{
                          padding: '12px',
                          borderTop: '1px solid var(--border)',
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          background: 'var(--bg-secondary)',
                        }}
                      >
                        <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                          Resolve conflict {activeConflictIndex + 1}:
                        </span>
                        <button
                          className="pill-btn"
                          onClick={() => resolveConflict(activeConflict.id, 'local')}
                          style={{
                            background: 'var(--success)',
                            color: 'white',
                            fontSize: '11px',
                          }}
                        >
                          Accept Local
                        </button>
                        <button
                          className="pill-btn"
                          onClick={() => resolveConflict(activeConflict.id, 'remote')}
                          style={{
                            background: 'var(--warning)',
                            color: 'white',
                            fontSize: '11px',
                          }}
                        >
                          Accept Remote
                        </button>
                        <button
                          className="pill-btn"
                          onClick={() => resolveConflict(activeConflict.id, 'both')}
                          style={{
                            background: 'var(--accent)',
                            color: 'white',
                            fontSize: '11px',
                          }}
                        >
                          Accept Both
                        </button>
                        <div style={{ flex: 1 }} />
                        <button
                          className="pill-btn"
                          onClick={() => resolveAllInFile('local')}
                          style={{ fontSize: '11px' }}
                        >
                          Accept All Local
                        </button>
                        <button
                          className="pill-btn"
                          onClick={() => resolveAllInFile('remote')}
                          style={{ fontSize: '11px' }}
                        >
                          Accept All Remote
                        </button>
                      </div>
                    )}

                    {activeConflict?.resolved && (
                      <div
                        style={{
                          padding: '12px',
                          borderTop: '1px solid var(--border)',
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          background: 'rgba(76, 175, 80, 0.1)',
                        }}
                      >
                        <span style={{ fontSize: '12px', color: 'var(--success)' }}>
                          Conflict resolved with: {activeConflict.resolution}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  /* Result view with editable textarea */
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div
                      style={{
                        padding: '8px 12px',
                        background: 'var(--bg-secondary)',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: 'var(--accent)',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      Merged Result (Editable)
                    </div>
                    <textarea
                      value={currentMerged}
                      onChange={(e) => handleMergedContentChange(e.target.value)}
                      style={{
                        flex: 1,
                        margin: 0,
                        padding: '12px',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        border: 'none',
                        resize: 'none',
                        background: 'var(--bg)',
                        color: 'var(--text)',
                        outline: 'none',
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--muted)',
                }}
              >
                No files with conflicts
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="pill-btn"
            onClick={saveMerge}
            disabled={!selectedFile || currentConflicts.some(c => !c.resolved)}
            style={{
              background: 'var(--success)',
              color: 'white',
              opacity: !selectedFile || currentConflicts.some(c => !c.resolved) ? 0.5 : 1,
            }}
          >
            Save This File
          </button>
          <button
            className="pill-btn"
            onClick={saveAllMerges}
            disabled={!allResolved}
            style={{
              background: 'var(--accent)',
              color: 'white',
              opacity: !allResolved ? 0.5 : 1,
            }}
          >
            Save All Merges
          </button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            {allResolved ? 'All conflicts resolved' : 'Resolve all conflicts to save'}
          </span>
          <button className="pill-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
});

export default ThreeWayMergeModal;
